import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  createFilterOptions,
} from "@mui/material";
import InputField from "components/Input/InputField";
import companyData from "const/companyData.json";
import { API_URL, currency } from "const/constants";
import useKothar from "context/useKothar";
import axios from "axios";
import { useAllProviders } from "./useAllProviders";
import { useMutation } from "@tanstack/react-query";
import { addDays } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import InvoiceTemplate from "./InvoiceTemplate";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const defaultCompany = companyData?.[0] || null;
const filter = createFilterOptions();

function admissionModuleValue(module) {
  if (module == null) return null;
  if (typeof module === "object") return module.value ?? module.label ?? null;
  return String(module);
}

function resolveClientDisplayName(accountPayload, listRow) {
  const candidates = [
    listRow?.clientName,
    listRow?.clientData?.name,
    accountPayload?.clientName,
    accountPayload?.clientData?.name,
  ];
  for (const n of candidates) {
    if (n != null && String(n).trim() !== "") return String(n).trim();
  }
  return "Client";
}

function commissionBonusFromTerm(term, row) {
  const commission = Number(term?.commission ?? row?.commission ?? 0);
  const bonus = Number(term?.bonus ?? row?.bonus ?? 0);
  const isCommissionClaimed = Boolean(
    term != null && term.isCommissionClaimed !== undefined
      ? term.isCommissionClaimed
      : row.isCommissionClaimed
  );
  const isBonusClaimed = Boolean(
    term != null && term.isBonusClaimed !== undefined ? term.isBonusClaimed : row.isBonusClaimed
  );
  return { commission, bonus, isCommissionClaimed, isBonusClaimed };
}

/** Stable across reloads so UI edits can be merged back in. */
function buildLineStableKey(clientAccountId, rowKey, termIndexLabel, lineKind) {
  return `${String(clientAccountId ?? "")}-${rowKey}-t${termIndexLabel}-${lineKind}`;
}

function pushCommissionBonusLines(lines, clientName, termIndexLabel, row, term, clientAccountId) {
  const { commission, bonus, isCommissionClaimed, isBonusClaimed } = commissionBonusFromTerm(
    term,
    row
  );
  const base = `${clientName} Term ${termIndexLabel}`;
  const rowKey = row.uuid || row.id || "row";

  // Only list amounts not yet claimed from the provider; GST defaults to yes per invoice line.
  if (!isCommissionClaimed) {
    const stableCommission = buildLineStableKey(
      clientAccountId,
      rowKey,
      termIndexLabel,
      "commission"
    );
    lines.push({
      id: stableCommission,
      stableKey: stableCommission,
      lineKind: "commission",
      description: `${base} — Commission`,
      amount: commission,
      total: commission,
      gst: "yes",
    });
  }
  if (!isBonusClaimed) {
    const stableBonus = buildLineStableKey(clientAccountId, rowKey, termIndexLabel, "bonus");
    lines.push({
      id: stableBonus,
      stableKey: stableBonus,
      lineKind: "bonus",
      description: `${base} — Bonus`,
      amount: bonus,
      total: bonus,
      gst: "yes",
    });
  }
}

/** Admission-only: unclaimed-from-provider commission/bonus only (up to two lines per term). */
export function buildAdmissionLinesFromAccount(accountPayload, listRow) {
  const clientName = resolveClientDisplayName(accountPayload, listRow);
  const clientAccountId = listRow?.clientAccountId ?? "";
  const details = accountPayload?.accountDetails || [];
  const lines = [];

  for (const row of details) {
    if (admissionModuleValue(row?.module) !== "Admission") continue;

    if (Array.isArray(row.admissionDetails) && row.admissionDetails.length > 0) {
      row.admissionDetails.forEach((term, idx) => {
        pushCommissionBonusLines(lines, clientName, idx + 1, row, term, clientAccountId);
      });
    } else {
      pushCommissionBonusLines(lines, clientName, 1, row, null, clientAccountId);
    }
  }

  return lines;
}

function lineAmountValue(line) {
  return Number(line.amount === "" || line.amount == null ? 0 : line.amount);
}

/** Keep GST / amount / description from a prior load when the same line is rebuilt from API. */
function mergePreservedInvoiceLines(freshLines, previousLines) {
  const prevByKey = new Map();
  for (const inv of previousLines || []) {
    const key = inv.stableKey ?? inv.id;
    if (key) prevByKey.set(key, inv);
  }
  return freshLines.map((inv) => {
    const key = inv.stableKey ?? inv.id;
    const old = key ? prevByKey.get(key) : null;
    if (!old) return inv;
    const amount = old.amount;
    const total = lineAmountValue({ amount });
    return {
      ...inv,
      gst: old.gst === "no" || old.gst === false ? "no" : "yes",
      amount,
      description: old.description,
      total,
    };
  });
}

function descriptionForPrint(line) {
  const amt = lineAmountValue(line);
  const base = line.description ?? "";
  return `${base} — ${currency} ${amt.toFixed(2)}`;
}

/** Print / save only lines with an amount > 0. */
function invoicesWithAmountForPrint(invoices) {
  return (invoices || []).filter((item) => lineAmountValue(item) > 0);
}

const BulkProviderInvoiceModal = ({ open, onClose, accountsList = [], providerList = [] }) => {
  const [, { refetchInvoiceList }] = useKothar();
  const contentRef = useRef(null);
  /** API account payloads by clientAccountId — avoids refetching on repeat "Load admission lines" while modal is open. Cleared when the modal opens. */
  const accountPayloadByClientIdRef = useRef({});
  const reactToPrintFn = useReactToPrint({
    contentRef,
    documentTitle: "Bulk provider invoice",
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    invoiceTo: "",
    address: "",
    contactNumber: "",
    abn: "",
    dueDate: addDays(new Date(), 7).toISOString().split("T")[0],
    invoices: [],
    issueDate: new Date().toISOString(),
    companyName: defaultCompany?.name || "",
    location: defaultCompany?.location || defaultCompany?.address || "",
    email: defaultCompany?.email || "",
    contact: defaultCompany?.contact || "",
    invoiceFromAbn: defaultCompany?.abn || "",
    paymentInfo: defaultCompany?.paymentInfo
      ? {
          accountNumber: defaultCompany.paymentInfo.accountNumber,
          accountName: defaultCompany.paymentInfo.accountName,
          bankDetails: defaultCompany.paymentInfo.bsb
            ? `BSB: ${defaultCompany.paymentInfo.bsb}`
            : defaultCompany.paymentInfo.bankName || "",
        }
      : { accountNumber: "", accountName: "", bankDetails: "" },
    status: false,
  });

  const fullProviderList = useAllProviders(open);

  const providerOptions = useMemo(() => {
    const source = fullProviderList.length ? fullProviderList : providerList || [];
    return source.map((p) => ({
      ...p,
      label: p?.universityName || p?.name || "",
      value: p?.universityName || p?.name || "",
    }));
  }, [fullProviderList, providerList]);

  const clientAccountOptions = useMemo(
    () =>
      Array.isArray(accountsList)
        ? accountsList.filter((a) => a?.clientAccountId && a?.clientName)
        : [],
    [accountsList]
  );

  const [selectedClients, setSelectedClients] = useState([]);

  const getClientOptionLabel = (option) => {
    if (!option) return "";
    const name = option.clientName || option?.clientData?.name;
    return option?.clientData?.passportNumber
      ? `${name} · ${option.clientData.passportNumber}`
      : name;
  };

  const loadLines = useCallback(async () => {
    const rows = selectedClients.filter((a) => a?.clientAccountId);
    if (!rows.length) {
      toast.info("Select one or more clients, then load lines.");
      return;
    }

    setLoading(true);
    const cache = accountPayloadByClientIdRef.current;
    const needFetch = rows.filter((r) => r.clientAccountId && !cache[r.clientAccountId]);

    try {
      if (needFetch.length > 0) {
        const responses = await Promise.all(
          needFetch.map((a) =>
            axios.get(`${API_URL}/accounts/${a.clientAccountId}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            })
          )
        );

        for (let i = 0; i < responses.length; i++) {
          const id = needFetch[i].clientAccountId;
          const payload = responses[i]?.data?.data;
          if (payload && id) {
            cache[id] = payload;
          }
        }
      }

      const merged = [];
      for (const listRow of rows) {
        const id = listRow.clientAccountId;
        const payload = id ? cache[id] : null;
        if (!payload) continue;
        merged.push(...buildAdmissionLinesFromAccount(payload, listRow));
      }

      setData((prev) => ({
        ...prev,
        invoices: mergePreservedInvoiceLines(merged, prev.invoices || []),
      }));

      if (rows.length > 0 && needFetch.length > 0 && rows.length > needFetch.length) {
        toast.info(
          `Cached ${rows.length - needFetch.length} client(s); fetched ${needFetch.length} new.`
        );
      }

      if (!merged.length) {
        toast.info(
          "No unclaimed admission commission or bonus found for the selected clients (or none selected)."
        );
      }
    } catch (e) {
      toast.error("Could not load account details for bulk invoice.");
      setData((prev) => ({ ...prev, invoices: [] }));
    } finally {
      setLoading(false);
    }
  }, [selectedClients]);

  useEffect(() => {
    if (!open) return;
    accountPayloadByClientIdRef.current = {};
    setSelectedClients([]);
    setData((prev) => ({
      ...prev,
      invoiceTo: "",
      address: "",
      contactNumber: "",
      abn: "",
      dueDate: addDays(new Date(), 7).toISOString().split("T")[0],
      issueDate: new Date().toISOString(),
      invoices: [],
    }));
  }, [open]);

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDelete = (lineId) => {
    setData((prev) => ({
      ...prev,
      invoices: (prev.invoices || []).filter((inv) => inv.id !== lineId),
    }));
    toast.success("Line removed");
  };

  const handleLineChange = (e, index) => {
    const { name, value } = e.target;
    setData((prev) => {
      const list = [...(prev.invoices || [])];
      const row = list[index];
      if (!row) return prev;

      const next = { ...row, [name]: value };
      if (name === "amount") {
        next.amount = value === "" ? "" : value;
        next.total = lineAmountValue(next);
      }
      list[index] = next;
      return { ...prev, invoices: list };
    });
  };

  const handleGstChange = (e, index) => {
    const { value } = e.target;
    setData((prev) => {
      const list = [...(prev.invoices || [])];
      const row = list[index];
      if (!row) return prev;
      list[index] = { ...row, gst: value === "no" ? "no" : "yes" };
      return { ...prev, invoices: list };
    });
  };

  const printData = useMemo(() => {
    const printableLines = invoicesWithAmountForPrint(data.invoices);
    const invoices = printableLines.map((item) => {
      const amt = lineAmountValue(item);
      return {
        description: descriptionForPrint(item),
        quantity: 1,
        price: amt,
        gst: item.gst === "no" || item.gst === false ? "no" : "yes",
        total: amt,
      };
    });

    const subTotal = invoices.reduce((sum, item) => sum + Number(item.total ?? 0), 0);
    return {
      ...data,
      invoiceTo: data.invoiceTo,
      invoices,
      subTotal,
      companyName: data.companyName || defaultCompany?.name,
      location: data.location || defaultCompany?.location,
      invoiceFromAbn: data.invoiceFromAbn || data.abn || defaultCompany?.abn,
      email: data.email || defaultCompany?.email,
      contact: data.contact || data.contactNumber || defaultCompany?.contact,
      paymentInfo:
        data.paymentInfo?.accountName != null
          ? data.paymentInfo
          : {
              accountName: defaultCompany?.paymentInfo?.accountName,
              accountNumber: defaultCompany?.paymentInfo?.accountNumber,
              bankDetails: defaultCompany?.paymentInfo?.bsb
                ? `BSB: ${defaultCompany.paymentInfo.bsb}`
                : "",
            },
      issueDate: data.issueDate || new Date().toISOString(),
      dueDate: data.dueDate || "",
      status: Boolean(data.status),
      invoiceId: new Date().getTime().toString().slice(-6),
    };
  }, [data]);

  const { mutate } = useMutation(postData, {
    onSuccess() {
      refetchInvoiceList();
      toast.success("Invoice saved");
    },
    onError() {
      toast.error("Error submitting invoice");
    },
  });

  async function postData(payload) {
    await axios.post(`${API_URL}/invoice`, payload);
  }

  const validateAndBuildPayload = () => {
    const hasAnyLines = (data.invoices || []).length > 0;
    const withAmountCount = invoicesWithAmountForPrint(data.invoices).length;
    if (!printData?.invoices?.length) {
      if (hasAnyLines && withAmountCount === 0) {
        toast.error(
          "Enter an amount greater than zero on at least one line to include it on the invoice."
        );
      } else {
        toast.error("Add at least one line item");
      }
      return null;
    }
    const to = (data.invoiceTo || "").trim();
    if (!to) {
      toast.error("Provider (invoice to) is required");
      return null;
    }
    const payload = { ...printData, invoiceTo: to };
    delete payload.invoiceId;
    return payload;
  };

  const handlePrint = () => {
    const payload = validateAndBuildPayload();
    if (!payload) return;
    reactToPrintFn?.();
  };

  const handlePrintAndSave = () => {
    const payload = validateAndBuildPayload();
    if (!payload) return;
    reactToPrintFn?.();
    mutate({ ...payload });
  };

  return (
    <>
      <Dialog
        open={Boolean(open)}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle className="font-bold uppercase">Create bulk invoice (Admission)</DialogTitle>
        <hr />
        <DialogContent>
          <p className="text-sm text-app-text-secondary mb-3">
            Select clients, then <strong>Load admission lines</strong>. Only commission and bonus
            that are <strong>not yet claimed from the provider</strong> are listed (qty 1 each). Set{" "}
            <strong>GST</strong> per row (default Yes). Rows with an amount greater than zero are
            included on the printed/saved invoice.
          </p>

          <div className="grid grid-cols-12 items-end gap-4 mb-4">
            <div className="col-span-12">
              <Autocomplete
                multiple
                size="small"
                options={clientAccountOptions}
                value={selectedClients}
                onChange={(_, v) => setSelectedClients(v)}
                getOptionLabel={getClientOptionLabel}
                isOptionEqualToValue={(a, b) => a?.clientAccountId === b?.clientAccountId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Clients"
                    placeholder="Search and select clients on this page…"
                  />
                )}
                disabled={loading}
              />
            </div>
            <div className="col-span-12 md:col-span-6">
              <Autocomplete
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                size="small"
                options={providerOptions}
                value={null}
                inputValue={data.invoiceTo ?? ""}
                onInputChange={(_, v, reason) => {
                  if (reason === "reset") return;
                  setData((prev) => ({ ...prev, invoiceTo: v }));
                }}
                filterOptions={(opts, params) => {
                  const filtered = filter(opts, params);
                  const { inputValue } = params;
                  const isExisting = opts.some((option) => option.label === inputValue);
                  if (inputValue !== "" && !isExisting) {
                    filtered.push({
                      label: inputValue,
                      value: inputValue,
                      inputValue,
                    });
                  }
                  return filtered;
                }}
                onChange={(_, newValue) => {
                  if (newValue == null) {
                    setData((prev) => ({ ...prev, invoiceTo: "" }));
                    return;
                  }
                  const text =
                    typeof newValue === "string"
                      ? newValue
                      : newValue.inputValue ?? newValue.label ?? newValue.value ?? "";
                  setData((prev) => ({
                    ...prev,
                    invoiceTo: text,
                    address: newValue?.universityDetail?.state,
                    abn: newValue?.universityDetail?.abn,
                  }));
                }}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  if (option?.inputValue) return option.inputValue;
                  return option?.label || option?.value || "";
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Provider (invoice to)"
                    placeholder="Type or select"
                  />
                )}
                loading={loading}
              />
            </div>
            <div className="col-span-12 md:col-span-3">
              <InputField
                label="Address"
                placeholder="Address"
                name="address"
                type="text"
                value={data?.address ?? ""}
                onChange={handleHeaderChange}
              />
            </div>
            <div className="col-span-12 md:col-span-3">
              <InputField
                label="Contact number"
                placeholder="Contact"
                name="contactNumber"
                type="text"
                value={data?.contactNumber ?? ""}
                onChange={handleHeaderChange}
              />
            </div>
            <div className="col-span-12 md:col-span-3">
              <InputField
                label="Due date"
                name="dueDate"
                type="date"
                value={data?.dueDate ?? ""}
                onChange={handleHeaderChange}
              />
            </div>
            <div className="col-span-12 md:col-span-3">
              <InputField
                label="ABN (optional)"
                name="abn"
                type="text"
                value={data?.abn ?? ""}
                onChange={handleHeaderChange}
              />
            </div>
            <div className="col-span-12 md:col-span-6 flex items-end gap-2">
              <Button variant="outlined" onClick={loadLines} disabled={loading}>
                Load admission lines
              </Button>
            </div>
          </div>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount (qty 1)</TableCell>
                  <TableCell>GST</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6}>Loading admission lines for selected clients…</TableCell>
                  </TableRow>
                )}
                {!loading && !(data.invoices || []).length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      Select clients and click Load admission lines. Rows are only unclaimed
                      provider commission/bonus (e.g. Ashish Gurung Term 1 — Commission).
                    </TableCell>
                  </TableRow>
                )}
                {(data.invoices || []).map((inv, index) => (
                  <TableRow key={inv.id}>
                    <TableCell sx={{ minWidth: 200 }}>
                      <InputField
                        label=""
                        name="description"
                        value={inv.description ?? ""}
                        onChange={(e) => handleLineChange(e, index)}
                        className="!mb-0"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 110 }}>
                      <span className="text-sm capitalize text-app-text">
                        {inv.lineKind === "bonus" ? "Bonus" : "Commission"}
                      </span>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <InputField
                        label=""
                        name="amount"
                        type="number"
                        startIcon={currency}
                        value={inv.amount === "" || inv.amount == null ? "" : String(inv.amount)}
                        onChange={(e) => handleLineChange(e, index)}
                        className="!mb-0"
                      />
                    </TableCell>
                    <TableCell sx={{ width: 150 }}>
                      <FormControl size="small" sx={{ minWidth: 0 }}>
                        <RadioGroup
                          row
                          value={inv.gst === "no" ? "no" : "yes"}
                          onChange={(e) => handleGstChange(e, index)}
                          sx={{
                            "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                            gap: 0.5,
                          }}
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio size="small" />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio size="small" />}
                            label="No"
                          />
                        </RadioGroup>
                      </FormControl>
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>
                      <span className="text-sm font-semibold">{currency} </span>
                      {lineAmountValue(inv).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove line">
                        <IconButton color="error" size="small" onClick={() => handleDelete(inv.id)}>
                          <AiOutlineDelete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions className="py-2 px-3 flex-wrap gap-2">
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button variant="contained" onClick={handlePrintAndSave} disabled={loading}>
            Print &amp; save invoice
          </Button>
          <Button variant="contained" onClick={handlePrint} disabled={loading}>
            Print / preview
          </Button>
        </DialogActions>
      </Dialog>

      <div
        className="print:hidden"
        style={{ position: "absolute", left: "-9999px", top: 0, width: "210mm" }}
      >
        <InvoiceTemplate printData={printData} contentRef={contentRef} />
      </div>
    </>
  );
};

export default BulkProviderInvoiceModal;
